// Pure geometry for straightening a photographed card. Corners are normalised to the
// photo (0..1) in the order top-left, top-right, bottom-right, bottom-left.

export const MIN_GAP = 0.1; // how close two neighbouring corners may get

export const defaultCorners = (inset = 0.04) => [
  { x: inset, y: inset },
  { x: 1 - inset, y: inset },
  { x: 1 - inset, y: 1 - inset },
  { x: inset, y: 1 - inset },
];

const clamp = (v, lo, hi) => Math.min(Math.max(v, lo), hi);

// Move one corner to `point`, keeping it inside the photo and the quad from folding
// over itself (corners keep their side of their neighbours).
export const moveCorner = (corners, index, point) => {
  const [tl, tr, br, bl] = corners;
  let { x, y } = point;
  x = clamp(x, 0, 1);
  y = clamp(y, 0, 1);

  if (index === 0) {
    x = Math.min(x, Math.min(tr.x, br.x) - MIN_GAP);
    y = Math.min(y, Math.min(bl.y, br.y) - MIN_GAP);
  } else if (index === 1) {
    x = Math.max(x, Math.max(tl.x, bl.x) + MIN_GAP);
    y = Math.min(y, Math.min(bl.y, br.y) - MIN_GAP);
  } else if (index === 2) {
    x = Math.max(x, Math.max(tl.x, bl.x) + MIN_GAP);
    y = Math.max(y, Math.max(tl.y, tr.y) + MIN_GAP);
  } else {
    x = Math.min(x, Math.min(tr.x, br.x) - MIN_GAP);
    y = Math.max(y, Math.max(tl.y, tr.y) + MIN_GAP);
  }

  x = clamp(x, 0, 1);
  y = clamp(y, 0, 1);
  return corners.map((c, i) => (i === index ? { x, y } : c));
};

const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);

// Size of the flat card in pixels: average of opposite edges, scaled to fit maxSide.
export const flatSize = (corners, photoWidth, photoHeight, maxSide = 1600) => {
  const px = corners.map((c) => ({ x: c.x * photoWidth, y: c.y * photoHeight }));
  const w = (dist(px[0], px[1]) + dist(px[3], px[2])) / 2;
  const h = (dist(px[0], px[3]) + dist(px[1], px[2])) / 2;
  const scale = Math.min(1, maxSide / Math.max(w, h));
  return { width: Math.max(1, Math.round(w * scale)), height: Math.max(1, Math.round(h * scale)) };
};

// Solve the 8 unknowns of the homography that maps `from` points onto `to` points.
// Returns [h0..h7] with h8 = 1, so x' = (h0x + h1y + h2) / (h6x + h7y + 1).
export const solveHomography = (from, to) => {
  const A = [];
  for (let i = 0; i < 4; i += 1) {
    const { x, y } = from[i];
    const { x: u, y: v } = to[i];
    A.push([x, y, 1, 0, 0, 0, -u * x, -u * y, u]);
    A.push([0, 0, 0, x, y, 1, -v * x, -v * y, v]);
  }
  for (let col = 0; col < 8; col += 1) {
    let pivot = col;
    for (let r = col + 1; r < 8; r += 1) if (Math.abs(A[r][col]) > Math.abs(A[pivot][col])) pivot = r;
    if (Math.abs(A[pivot][col]) < 1e-12) return null;
    [A[col], A[pivot]] = [A[pivot], A[col]];
    for (let r = 0; r < 8; r += 1) {
      if (r === col) continue;
      const f = A[r][col] / A[col][col];
      for (let c = col; c < 9; c += 1) A[r][c] -= f * A[col][c];
    }
  }
  return A.map((row, i) => row[8] / row[i]);
};

// Gentle clean-up for a flattened card, in place: a photo of white paper is usually grey
// and flat. Stretch brightness so the paper reads as paper, with the same gain on every
// channel so colours (logos!) keep their hue. Dark cards are only lightly stretched, and
// nothing happens when the photo has hardly any contrast.
export const autoLevels = (rgba, { clip = 0.01, maxGain = 1.5, minRange = 40, paperWhite = 140 } = {}) => {
  const pixels = rgba.length / 4;
  const histogram = new Uint32Array(256);
  for (let i = 0; i < rgba.length; i += 4) {
    histogram[Math.floor((rgba[i] * 299 + rgba[i + 1] * 587 + rgba[i + 2] * 114) / 1000)] += 1;
  }

  let lo = 0;
  let hi = 255;
  for (let seen = 0; lo < 255 && seen + histogram[lo] < pixels * clip; lo += 1) seen += histogram[lo];
  for (let seen = 0; hi > 0 && seen + histogram[hi] < pixels * clip; hi -= 1) seen += histogram[hi];

  const range = hi - lo;
  if (range < minRange) return rgba;
  const gain = Math.min(255 / range, maxGain);
  // Bright photo: pin the paper to white. Dark photo: only stretch away from the black point.
  const offset = hi >= paperWhite ? 255 - hi * gain : -lo * gain;

  for (let i = 0; i < rgba.length; i += 4) {
    rgba[i] = rgba[i] * gain + offset;
    rgba[i + 1] = rgba[i + 1] * gain + offset;
    rgba[i + 2] = rgba[i + 2] * gain + offset;
  }
  return rgba;
};

// Fill a dstW x dstH RGBA buffer by sampling the photo through the homography
// (flat card pixel -> photo pixel), with bilinear smoothing.
export const warpPixels = (src, srcW, srcH, dstW, dstH, h) => {
  const out = new Uint8ClampedArray(dstW * dstH * 4);
  const maxX = srcW - 1;
  const maxY = srcH - 1;
  for (let y = 0; y < dstH; y += 1) {
    for (let x = 0; x < dstW; x += 1) {
      const d = h[6] * x + h[7] * y + 1;
      const sx = clamp((h[0] * x + h[1] * y + h[2]) / d, 0, maxX);
      const sy = clamp((h[3] * x + h[4] * y + h[5]) / d, 0, maxY);
      const x0 = Math.floor(sx);
      const y0 = Math.floor(sy);
      const x1 = Math.min(x0 + 1, maxX);
      const y1 = Math.min(y0 + 1, maxY);
      const fx = sx - x0;
      const fy = sy - y0;
      const o = (y * dstW + x) * 4;
      for (let c = 0; c < 4; c += 1) {
        const top = src[(y0 * srcW + x0) * 4 + c] * (1 - fx) + src[(y0 * srcW + x1) * 4 + c] * fx;
        const bottom = src[(y1 * srcW + x0) * 4 + c] * (1 - fx) + src[(y1 * srcW + x1) * 4 + c] * fx;
        out[o + c] = top * (1 - fy) + bottom * fy;
      }
    }
  }
  return out;
};
